<template>
  <div class="d-flex max-w-5xl mx-auto">
    <div class="ai-shadow-chat-pro flex-1">

      <header class="lesson-header">
        <div class="progress-section d-flex align-items-center w-100" v-if="aiCurrentTurn && aiCurrentTurn.progress">
          <span class="progress-label">課程目標</span>
          <div class="progress-wrapper position-relative flex-grow-1">
            <div class="progress custom-progress">
              <div
                class="progress-bar"
                :style="{ width: (aiCurrentTurn.progress.current / aiCurrentTurn.progress.total * 100) + '%' }"
              ></div>
            </div>
          </div>
          <span class="counter ml-3">{{ aiCurrentTurn.progress.current }}/{{ aiCurrentTurn.progress.total }}</span>
        </div>
      </header>

      <!-- 對話區域 -->
      <div class="chat-scroll-area bg-white mx-4" ref="chatArea">
        <div
          v-for="(msg, index) in chatHistory"
          :key="index"
          :class="['message-row', msg.role]"
        >
          <!-- 老師的頭像 -->
          <div class="avatar" v-if="msg.role === 'teacher'">
            <div class="avatar-placeholder"><img src="@/assets/image/ai/robat_icon.png" class="" style="width: 40px;"></div>
          </div>

          <div class="bubble-container">
            <!-- AI 老師的泡泡 (單句模式) -->
            <div class="bubble" :class="{ 'target-bubble': msg.isTarget }" v-if="msg.role === 'teacher'">
              <div class="content">
                <span class="text pr-1 whitespace-pre-wrap" v-if="!msg.isTarget">
                  {{ msg.en }}
                </span>
                <div v-if="msg.isTarget" class="target-indicator mt-1 whitespace-pre-wrap">
                  ➔ <p class="whitespace-pre-wrap font-weight-bold">{{ msg.en }}</p>
                </div>
              </div>

              <div class="actions">
                <button class="icon-btn" @click="playTTS(msg.en)">
                  <img src="@/assets/image/ai/btn_AItalk_play_perple.png" alt="play">
                </button>
                <button class="icon-btn" @click="toggleTranslate(index)">
                  <!-- 根據 showZh 切換翻譯按鈕圖示 -->
                  <img v-if="msg.showZh" src="@/assets/image/ai/btn_AItalk_CN_active_perple.png" alt="translate active">
                  <img v-else src="@/assets/image/ai/btn_AItalk_CN_perple.png" alt="translate">
                </button>
              </div>

              <!-- 中文翻譯 -->
              <div class="zh-translation" v-if="msg.showZh">
                <div :class="{'font-weight-bold': msg.isTarget}">
                  <span v-if="msg.isTarget">➔ </span>{{ msg.zh }}
                </div>
              </div>
            </div>

            <!-- 使用者的泡泡 (念完後產生的結果) -->
            <div class="bubble user-bubble" v-if="msg.role === 'user'">
              <div class="content font-weight-bold" v-html="msg.coloredHtml"></div>
              <div class="actions justify-end">
                <button class="icon-btn highlight" @click="openFeedbackModal(msg.feedback)">
                  <img src="@/assets/image/ai/btn_AItalk_voice_star.png" alt="star" />
                </button>
                <button class="icon-btn" @click="playUserAudio(msg.audioUrl)" v-if="msg.audioUrl">
                  <img src="@/assets/image/ai/btn_AItalk_voice.png" alt="voice" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 讀取中動畫 -->
        <div v-if="isAiTeacherLoading" class="loading-indicator">
          <span class="dot"></span><span class="dot"></span><span class="dot"></span>
        </div>
      </div>

      <!-- 底部跟讀與錄音區 -->
      <div class="bottom-panel bg-white mx-4 mt-1">
        <div class="target-sentence-box" v-if="aiCurrentTurn && aiCurrentTurn.target && !aiCurrentTurn.end">
          <div class="box-label">跟讀這一句</div>
          <div class="box-content">
            <button class="play-target-btn" @click="playTTS(aiCurrentTurn.target.en)">
              <img src="@/assets/image/ai/btn_AItalk_play_perple.png">
            </button>
            <span class="target-text">{{ aiCurrentTurn.target.en }}</span>
          </div>
        </div>

        <div class="record-wrapper" v-if="aiCurrentTurn && aiCurrentTurn.expect_user !== 'none'">
          <AiRecordButton
            class="pro-record-btn"
            :disabled="isAiTeacherLoading || isAiFeedbackLoading"
            :s-text="aiCurrentTurn.target ? aiCurrentTurn.target.en : ''"
            @record-complete="onRecordComplete"
          />
        </div>

        <div class="record-wrapper" v-if="aiCurrentTurn && aiCurrentTurn.end">
          <button class="pro-record-btn finish" @click="finishLesson">完成課程</button>
        </div>
      </div>
    </div>

    <!-- 右側工具列 (維持原樣) -->
    <div>
      <a class="tips_btn d-inline-block pointer mt-5" @click="toggleTips">
        <img src="@/assets/image/ai/tips_btn.png" alt="" class=" object-contain">
      </a>
      <testingToolsSWAL />
    </div>
  </div>
</template>

<script>
import api from '@/store/api'
import aiShadowMixin from '@/mixins/aiShadowMixin';
import allJs from '@/components/AiHelper/all.js';
import AiRecordButton from '@/components/AiHelper/chat/AiRecordButton.vue';
import testingToolsSWAL from '@/components/AiHelper/chat/testingToolsSWAL'
import Chart from 'chart.js'

export default {
  name: 'AiShadowChat',
  mixins: [aiShadowMixin, allJs],
  components: {
    AiRecordButton,
    testingToolsSWAL
  },
  props: {
    view_id: {
      type: String
    }
  },
  data() {
    return {
      categoryContext: null,
      isTtsPlaying: false,
      currentAudioElement: null,
      chatHistory: [],
      currentFeedbackData: {},
      lastUserAudioUrl: null,
      prompt_id: null
    };
  },
  beforeDestroy() {
    if (this.currentAudioElement) {
      this.currentAudioElement.pause();
      this.currentAudioElement = null;
    }
  },
  async mounted() {
    await this.$store.dispatch('getRemainPoints')
    await this.fetchCategoryContext(true);
    this.initFirstTurn();
  },
  watch: {
    aiCurrentFeedback: {
      handler(newFeedback) {
        if (newFeedback) {
          const lastUserMsg = [...this.chatHistory].reverse().find(m => m.role === 'user');
          if (lastUserMsg && lastUserMsg.feedback) {
            lastUserMsg.feedback.overall = newFeedback.overall;
            lastUserMsg.feedback.tips = newFeedback.tips;
          }
        }
      },
      deep: true
    }
  },
  methods: {
    async fetchCategoryContext (fromQuery = false) {
      const path = `/ai/categories/${this.view_id}`
      const options = { headers: { showLoading: fromQuery } }

      try {
        const res = await api.get(path, options);
        await new Promise(resolve => setTimeout(resolve, 500));

        this.prompt_id = res.data.prompt_id;
        this.categoryContext = {
          unit_ch: res.data.data.name,
          unit_en: res.data.data.name_en,
          context: res.data.data.context,
          context2: res.data.data.context2,
          theme: res.data.data.theme,
          guide: res.data.data.guide
        };

      } catch (err) {
        console.error('fetchCategoryContext: ', err);
      }
    },
    async fetchTeacherTurn (payload) {
      this.isAiTeacherLoading = true;
      try {
        const res = await api.post(`/ai/generate/eb418a76-30f7-4430-a0e6-7167f4b79e08`, payload);

        const responseData = res.data || {};
        if (responseData.session) {
          this.aiSession = responseData.session;
        }

        let parsedTurn = this.parseAiResult(responseData.result);
        parsedTurn = this._coerceTargetRole(parsedTurn);

        this.aiCurrentTurn = parsedTurn;

        if (parsedTurn && parsedTurn.lines && parsedTurn.lines.length > 0) {
          const newMessages = parsedTurn.lines.map(line => ({
            role: 'teacher',
            en: line.en,
            zh: line.zh,
            isTarget: line.role === 'target' || line.role === 'correction',
            showZh: false,
          }));

          this.chatHistory.push(...newMessages);
          this.scrollToBottom();
        }

      } catch (error) {
        console.log('fetchTeacherTurn: ' + error.response.data.msg)
        // alert(error.response.data.msg)
      } finally {
        this.isAiTeacherLoading = false;
      }
    },
    async initFirstTurn() {
      if (!this.categoryContext) return;
      const firstTurnPayload = { content: "", session: "", ...this.categoryContext };
      await this.fetchTeacherTurn(firstTurnPayload);
    },

    buildErsDigest(azureRaw) {
      const root = azureRaw?.data ?? azureRaw;
      const scores = root?.Scores ?? {};
      const words = root?.Words ?? root?.WordScores ?? root?.NBest?.[0]?.Words ?? [];

      const allWords = words.map(w => ({
        word: w.Word ?? w.word ?? '',
        score: Number(w.AccuracyScore ?? w.Score ?? 0),
        error: w.ErrorType ?? w.error_type ?? 'None',
      }));

      const weak_words = allWords.filter(w => w.score < 80 || (w.error && w.error !== 'None'));

      const prosodySet = new Set();
      for (const w of words) {
        const p = w.Feedback?.Prosody;
        const breakErrors = p?.Break?.ErrorTypes ?? [];
        const intonationErrors = p?.Intonation?.ErrorTypes ?? [];
        [...breakErrors, ...intonationErrors].forEach(t => {
          if (t && t !== 'None') prosodySet.add(t);
        });
      }

      const originalText = root?.Text || '';
      let coloredTextHtml = originalText;

      if (allWords.length > 0 && originalText) {
        allWords.forEach(wItem => {
          if (!wItem.word) return;
          let colorClass = 'text-success';
          if (wItem.score < 60) colorClass = 'text-danger';
          else if (wItem.score < 80) colorClass = 'text-warning';
          const regex = new RegExp(`\\b(${wItem.word})\\b`, 'i');
          coloredTextHtml = coloredTextHtml.replace(regex, `<span class="${colorClass}">$1</span>`);
        });
      }

      return {
        score: Number(root?.Score ?? 0),
        accuracy: Number(scores.AccuracyScore ?? 0),
        fluency: Number(scores.FluencyScore ?? 0),
        completeness: Number(scores.CompletenessScore ?? 0),
        pronunciation: Number(scores.PronScore ?? 0),
        prosody: Number(scores.ProsodyScore ?? 0),
        target_text: root?.stext ?? root?.sText ?? null,
        recognized: originalText,
        weak_words,
        prosody_issues: Array.from(prosodySet),
        coloredHtml: coloredTextHtml
      };
    },

    onRecordComplete(azureRawErsData) {
      if (!this.aiCurrentTurn || !this.aiCurrentTurn.target) return;

      const lastMessage = this.chatHistory[this.chatHistory.length - 1];
      if (!lastMessage || lastMessage.role !== 'teacher') {
        console.warn('最後一筆訊息不是老師，阻擋重複送出錄音！');
        return;
      }

      const digest = this.buildErsDigest(azureRawErsData);
      const targetEn = this.aiCurrentTurn.target.en;

      this.chatHistory.push({
        role: 'user',
        coloredHtml: digest.coloredHtml,
        audioUrl: azureRawErsData?.data?.AudioUrl || azureRawErsData?.AudioUrl || this.lastUserAudioUrl,
        feedback: {
          score: digest.score,
          details: {
            pronunciation: digest.pronunciation,
            accuracy: digest.accuracy,
            fluency: digest.fluency,
            completeness: digest.completeness,
            prosody: digest.prosody
          },
          overall: '分析中...',
          tips: []
        }
      });

      this.scrollToBottom();
      this.processErsResult(digest, targetEn, this.categoryContext);
    },

    toggleTranslate(index) {
      this.$set(this.chatHistory[index], 'showZh', !this.chatHistory[index].showZh);
    },

    openFeedbackModal(feedbackData) {
      if (typeof Chart === 'undefined') {
        this.$swal("錯誤", "圖表套件載入失敗", "error");
        return;
      }

      const details = feedbackData.details;
      const score = feedbackData.score;

      let tipsHtml = '';
      if (feedbackData.overall || (feedbackData.tips && feedbackData.tips.length > 0)) {
        tipsHtml = `
          <div class="text-left mt-4 p-3 rounded" style="background-color: #FFF9ED; font-size: 15px; color: #444; text-align: left;">
            <p style="margin-bottom: 10px; font-weight: bold;">${feedbackData.overall || ''}</p>
            ${feedbackData.tips && feedbackData.tips.length ? `
              <h5 style="color: #5A5D9B; font-size: 15px; margin-bottom: 5px;">💡 口說重點提示</h5>
              <ul style="padding-left: 20px; margin: 0; color: #555;">
                ${feedbackData.tips.map(tip => `<li>• ${tip}</li>`).join('')}
              </ul>
            ` : ''}
          </div>
        `;
      }

      this.$swal({
        width: '600px',
        padding: '0',
        showCloseButton: true,
        showConfirmButton: false,
        showCancelButton: true,
        cancelButtonText: '繼續對話',
        customClass: {
          popup: 'rounded-lg overflow-hidden',
          actions: 'my-3',
          closeButton: 'top-0 right-0 z-2',
          cancelButton: 'btn btn-outline-danger px-5 rounded'
        },
        html: `
          <div class="result-header text-center row m-0" style="background-color: #ffd966; position: relative;">
            <div class="col d-flex align-items-end justify-content-center">
              <img src="${require('@/assets/image/ai/course/robot_progress.png')}" alt="robot" style="width: 100%;margin-bottom: -10px;">
            </div>
            <div class="col d-flex align-items-center justify-content-center">
              <div class="bg-white rounded shadow-sm d-inline-block px-4 py-2 my-4" style="min-width: 140px;">
                <div style="font-size: 14px; color: #333; font-weight: bold;">Score</div>
                <div style="font-size: 48px; font-weight: bold; line-height: 1;">${score}</div>
              </div>
            </div>
          </div>
          <div class="p-4 bg-white" style="position: relative;">
            <div style="width: 100%; height: 280px; margin: 0 auto;">
              <canvas id="resultChart"></canvas>
            </div>
            ${tipsHtml}
          </div>
        `,
        didOpen: () => {
          setTimeout(() => {
            const canvas = document.getElementById('resultChart');
            if (!canvas) return;

            const ctx = canvas.getContext('2d');
            new Chart(ctx, {
              type: 'radar',
              data: {
                labels: ['發音', '準確度', '流暢度', '完整性', '韻律'],
                datasets: [{
                  label: 'Score',
                  data: [details.pronunciation, details.accuracy, details.fluency, details.completeness, details.prosody],
                  backgroundColor: 'rgba(180, 185, 210, 0.5)',
                  borderColor: '#b4b9d2',
                  pointBackgroundColor: '#b4b9d2',
                  borderWidth: 1,
                  pointRadius: 0
                }]
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                legend: { display: false },
                scale: {
                  pointLabels: { fontSize: 16, fontColor: '#000000', fontStyle: 'bold' },
                  angleLines: { color: '#e0e0e0' },
                  gridLines: { color: '#e0e0e0' },
                  ticks: { display: false, min: 0, max: 100, stepSize: 20 }
                }
              }
            });
          }, 100);
        }
      });
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatArea;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    async playTTS(text) {
      if (this.isTtsPlaying) return;
      this.isTtsPlaying = true;
      try {
        const audioUrl = await this.getTTSUrl('female', text);
        if (audioUrl) {
          if (this.currentAudioElement) this.currentAudioElement.pause();
          this.currentAudioElement = new Audio(audioUrl);
          this.currentAudioElement.onended = () => { this.isTtsPlaying = false; };
          this.currentAudioElement.onerror = () => { this.isTtsPlaying = false; };
          this.currentAudioElement.play();
        } else {
          this.isTtsPlaying = false;
        }
      } catch (error) {
        console.error('TTS 失敗', error);
        this.isTtsPlaying = false;
      }
    },

    playUserAudio(url) {
      if (!url) {
        console.warn('找不到音檔網址');
        return;
      }
      try {
        if (this.currentAudioElement) this.currentAudioElement.pause();
        this.currentAudioElement = new Audio(url);
        this.currentAudioElement.play();
      } catch (error) {
        console.error('播放發生例外錯誤', error);
      }
    },

    finishLesson() {
      this.$router.push({ name: 'Home' })
    },

    toggleTips () {
      const htmlText = `
        <h6 class="font-bold text-lg">操作說明：</h6>
        <p class="mb-2">1. 點<img src="${require('@/assets/image/ai/mic_C.png')}" class="inline-block icon-img mx-1">說出回應的句子，再點
          <img src="${require('@/assets/image/ai/submit_icon.png')}" class="inline-block icon-img mx-1">，送出回應。</p>
        <p class="mb-2">2. 點<img src="${require('@/assets/image/ai/AI_A.png')}" class="inline-block icon-img mx-1">參考 AI 回應內容，並練習說說看。</p>
        <p class="mb-2">3. 點<img src="${require('@/assets/image/ai/translate_btn_off.png')}" class="inline-block icon-img mx-1">輸入想翻譯的中文。</p>
        <p class="mb-2">4. 點<img src="${require('@/assets/image/ai/btn_AItalk_voice.png')}" class="inline-block icon-img mx-1">聆聽自己的錄音。</p>
        <p class="mb-2">5. 點<img src="${require('@/assets/image/ai/btn_AItalk_correct.png')}" class="inline-block icon-img mx-1">觀看 AI 反饋建議，讓自己下次說得更好。</p>
        <p class="mb-2">6. 若 AI 回應不符預期，請修改文字內容或點擊重新整理。</p>
      `
      return this.$swal({
        html: htmlText,
        showDenyButton: false,
        focusConfirm: false,
        confirmButtonText: `關閉`,
        title: '溫馨提醒',
        position: window.innerWidth < 768 ? 'top' : 'center',
        customClass: {
          title: 'text-white text-2xl swal_tips',
          popup: 'p-0',
          header: 'justify-center items-center ',
          htmlContainer: 'my-4 text-left',
          actions: 'mb-4 mt-2',
          confirmButton: 'btn-deny'
        }
      })
    }
  }
}
</script>
<style scoped>
.ai-shadow-chat-pro {
  display: flex;
  flex-direction: column;
  /* height: calc(100vh - 80px); */
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* 頂部標題 */
.lesson-header {
  padding: 20px 30px;
  background: transparent;
}
.title-area h2 { font-size: 1.4rem; color: #333; margin: 0 0 5px 0; font-weight: bold; }
.title-area .subtitle { color: #666; margin: 0 0 15px 0; }
.progress-area { display: flex; align-items: center; font-size: 0.85rem; color: #555; }
.progress-label { margin-right: 15px; font-weight: bold; color: #333;}
.progress-bar-bg { flex: 1; height: 6px; background: #E0E5EC; border-radius: 3px; overflow: hidden; margin-right: 15px; }
.progress-bar-fill { height: 100%; background: #5A5D9B; transition: width 0.3s ease; }

/* 對話區域 */
.chat-scroll-area {
  flex: 1;
  overflow-y: auto;
  padding: 10px 30px 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 60vh;
  min-height: 60vh;
  max-height: 60vh;
  overflow-y: scroll;
}
.message-row { display: flex; align-items: flex-start; gap: 15px; width: 100%; }
.message-row.user { justify-content: flex-end; }
.avatar-placeholder { width: 40px; height: 40px; background: #5A5D9B; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 1.2rem; }
.avatar-placeholder.user { background: #ccc; }

.bubble {
  max-width: 700px;
  padding: 15px 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}
.message-row.teacher .bubble { background: #EAF0F6; border-top-left-radius: 0; }
.message-row.teacher .bubble.target-bubble {
  background: #DDE7F4;
  border: 1px solid #AEB9E8;
  box-shadow: 0 4px 12px rgba(90, 93, 155, 0.12);
}
.message-row.user .bubble { background: #FEF7E6; border-top-right-radius: 0; } /* 圖三的淺黃色 */

.content { font-size: 1.1rem; line-height: 1.5; color: #333; }
.target-indicator { margin-top: 10px; font-weight: bold; display: flex; gap: 8px; }
.zh-translation { margin-top: 10px; padding-top: 10px; border-top: 1px dashed #ccc; font-size: 0.9rem; color: #666; }

/* 按鈕群組 */
.actions { display: flex; margin-top: 12px; }
.actions.justify-end { justify-content: flex-end; }
.icon-btn { border: 0;; background: transparent; cursor: pointer; font-size: 0.9rem; display: flex; align-items: center; justify-content: center; }

/* 字體顏色 (對應發音分數) */
.text-green { color: #2b8a3e; }
.text-orange { color: #d9480f; }
.text-red { color: #c92a2a; }

/* 底部操作區 */
.bottom-panel { background: #F4F6F9; padding: 20px; }
.target-sentence-box { background: #EAF0F6; border-radius: 12px; padding: 15px 20px; margin-bottom: 20px; }
.box-label { font-size: 0.85rem; color: #5A5D9B; font-weight: bold; margin-bottom: 8px; }
.box-content { display: flex; align-items: center; gap: 15px; }
.play-target-btn { cursor: pointer; outline: 0; border: 0; }
.target-text { font-size: 1.1rem; font-weight: bold; color: #111; }

/* 錄音按鈕 (覆寫子元件的樣式) */
::v-deep .pro-record-btn {
  width: 100%;
  background: #5A5D9B !important; /* 設計稿的紫色 */
  padding: 18px !important;
  border-radius: 30px !important;
  font-size: 1.2rem !important;
  box-shadow: 0 4px 12px rgba(90, 93, 155, 0.3) !important;
}
::v-deep .pro-record-btn.is-recording { background: #e03131 !important; transform: scale(0.98); }
.finish { background: #2b8a3e !important; color: white; width: 100%; padding: 18px; border-radius: 30px; font-size: 1.2rem; font-weight: bold; border: none; cursor: pointer; }

/* 評分 Modal (對應圖二) */
.feedback-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.feedback-modal-content { background: white; width: 90%; max-width: 500px; border-radius: 20px; padding: 30px; position: relative; box-shadow: 0 10px 30px rgba(0,0,0,0.2); }
.close-btn { position: absolute; top: 15px; right: 20px; background: transparent; border: none; font-size: 1.5rem; cursor: pointer; color: #666; }
.modal-header { display: flex; justify-content: center; margin-bottom: 20px; }
.score-circle { background: white; border: 4px solid #FFF3E6; box-shadow: 0 4px 15px rgba(0,0,0,0.1); width: 120px; height: 120px; border-radius: 50%; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.score-label { font-size: 0.9rem; font-weight: bold; color: #333; }
.score-number { font-size: 2.5rem; font-weight: bold; color: #111; line-height: 1; }
.radar-chart-placeholder { height: 200px; background: #f9f9f9; border-radius: 10px; margin-bottom: 20px; border: 1px dashed #ddd; }
.feedback-text-area { background: #FFF9ED; padding: 15px; border-radius: 10px; margin-bottom: 20px; font-size: 0.95rem; line-height: 1.6; color: #444; }
.tips-section h5 { margin: 15px 0 5px; font-size: 0.95rem; color: #5A5D9B; }
.tips-section ul { padding-left: 20px; margin: 0; color: #555; }
.ok-btn { width: 100%; background: #E03131; color: white; border: none; padding: 14px; border-radius: 8px; font-size: 1.1rem; font-weight: bold; cursor: pointer; }

/* 進度條樣式 */
.custom-progress {
  height: 14px !important;
  background: transparent !important;
  border: 1.5px solid #5d669b;
  border-radius: 10px;
}
.custom-progress .progress-bar {
  background-color: #5d669b;
  border-radius: 10px;
}

/* 完成狀態的覆蓋按鈕 */
.complete-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
}
.btn-complete {
  background-color: #5d669b;
  color: white;
  border: 2px solid white;
  padding: 5px 20px;
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

/* 導師選中紅框 (image_dc4cde) */
.mentor-item.active {
  border: 2px solid #e5413c !important; /* 紅色外框 */
  box-shadow: 0 0 10px rgba(229, 65, 60, 0.2);
}

/* 彈窗紅色標題 */
.mentor-swal-header {
  background-color: #e5413c !important;
}
</style>
